/**
 * Redux duck for the public SEO landing pages (/dack/:make/:model,
 * /falgar/:make/:model). These pages live alongside the marketplace and
 * always link back into the live listings page (/annonser/:make/:model).
 *
 * loadData runs both server-side (SSR, so Googlebot gets full HTML) and on
 * the client. It pulls two things in parallel:
 *   1. Car info from the master database via our own /api/seo endpoints.
 *   2. A live slice of matching listings via the Sharetribe SDK, using the
 *      same category params (pub_categoryLevel1/2) the SearchPage uses, so
 *      the grid is always current.
 */
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { apiBaseUrl } from '../../util/api';
/* The same package test the cart, checkout and the plate-lookup suggestions use.
 * It needs BOTH the marker and a usable bundle: a listing carrying the marker
 * with no parts behind it cannot be fulfilled as a package and falls back to an
 * ordinary listing, so an answer page must not count it as one. Sharing the
 * function is what keeps this page's idea of "a package" from drifting away
 * from checkout's. */
import { isPackageListing } from '../../util/packageBundleCore';
import { MARKETPLACE_FILTER } from '../../util/catalogueCore';

// How many live listings to show in the on-page grid. The "see all" CTA
// sends the user to /annonser for the full, filterable result set.
const RESULT_PAGE_SIZE = 12;

// Which listing ad types are relevant per page type. "wheel" (complete
// rim+tire sets) is relevant to both tire and rim pages.
const AD_TYPES_BY_PAGE_TYPE = {
  tire: ['tire', 'wheel'],
  rim: ['rim', 'wheel'],
  /* A hjulpaket is a complete wheel, so a complete wheel is the only ad type it
   * can have. Bygg paket also publishes rim-only ("fälgpaket") and tyre-only
   * ("däckpaket") packages, and those are indexed as rim and tire — they are
   * deliberately NOT included, because a page headed "Hjulpaket till Volvo XC60"
   * listing a set of bare rims would be making the exact kind of wrong claim
   * this story exists to stop. Narrowing to packages happens below. */
  package: ['wheel'],
};

/* How many listings to read when the page needs a COUNT rather than a page of
 * cards (P25009-408 BR-5). 100 is the Marketplace API's per-page ceiling.
 *
 * Asking for packages directly needs a *search schema* for `isReachShell`. With
 * one, `pub_isReachShell=true` narrows the query and `meta.totalItems` is the
 * exact number of packages. Without one, the Marketplace API silently ignores a
 * `pub_` key it does not know and returns EVERYTHING, 200 OK — measured on the
 * test marketplace: 1314 listings with the filter, 1314 without, and 1314 with a
 * deliberately invented field. The same mistake was costing the reach reconciler
 * 65 wasted calls every five minutes (see packageReachPublisher.js).
 *
 * So the parameter is sent, and then `filterTookEffect` below checks whether it
 * did anything rather than assuming. That keeps the page honest in either
 * environment: exact where the schema exists, and the old scan-and-count where
 * it does not, instead of confidently reporting a count of every wheel listing
 * because someone deployed before running the CLI. */
const PACKAGE_SCAN_SIZE = 100;

// ================ Action types ================ //

export const FETCH_SEO_REQUEST = 'app/SeoPages/FETCH_SEO_REQUEST';
export const FETCH_SEO_SUCCESS = 'app/SeoPages/FETCH_SEO_SUCCESS';
export const FETCH_SEO_ERROR = 'app/SeoPages/FETCH_SEO_ERROR';

export const FETCH_DEMAND_CARS_SUCCESS = 'app/SeoPages/FETCH_DEMAND_CARS_SUCCESS';

// ================ Reducer ================ //

const initialState = {
  pageType: 'tire',
  fetchInProgress: false,
  fetchError: null,
  fitment: null,
  relatedModels: [],
  listingRefs: [],
  /* The live figures the answer page states as fact (P25009-408 BR-5). Held
   * separately from listingRefs because the cards are a slice and these are
   * about the whole set: how many exist, and the lowest price among them. */
  liveTotal: 0,
  liveTotalIsAtLeast: false,
  liveFromPrice: null,
  // The ranked car list behind the /hjulpaket index page.
  demandCars: [],
  // Which car the data in state belongs to, so a refetch of the same car can
  // be told apart from a navigation to a different one.
  loadedFor: null,
};

/** Key of the page the data belongs to, e.g. "tire:volvo:xc60". */
const pageKey = (pageType, make, model) =>
  make && model ? `${pageType}:${String(make).toLowerCase()}:${String(model).toLowerCase()}` : null;

export default function seoPagesReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_SEO_REQUEST: {
      const requestedKey = pageKey(payload.pageType, payload.make, payload.model);
      // Navigating to a different car must not show the previous car's info,
      // so that data is cleared. Refetching the car already in state is a
      // different thing: it happens on every page load, because the client
      // runs loadData again for the page the server just rendered. Clearing
      // there replaced the whole page with an empty shell for about a second
      // and then put it back, which was worth 0.57 CLS on desktop - the
      // single largest layout shift on these pages.
      const isSameCar = requestedKey != null && requestedKey === state.loadedFor;
      const keptData = isSameCar
        ? {
            fitment: state.fitment,
            relatedModels: state.relatedModels,
            listingRefs: state.listingRefs,
            liveTotal: state.liveTotal,
            liveTotalIsAtLeast: state.liveTotalIsAtLeast,
            liveFromPrice: state.liveFromPrice,
          }
        : {
            fitment: null,
            relatedModels: [],
            listingRefs: [],
            liveTotal: 0,
            liveTotalIsAtLeast: false,
            liveFromPrice: null,
          };

      return {
        ...state,
        pageType: payload.pageType,
        fetchInProgress: true,
        fetchError: null,
        loadedFor: isSameCar ? state.loadedFor : null,
        ...keptData,
      };
    }
    case FETCH_SEO_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        fitment: payload.fitment,
        relatedModels: payload.relatedModels,
        listingRefs: payload.listingRefs,
        liveTotal: payload.liveTotal || 0,
        liveTotalIsAtLeast: !!payload.liveTotalIsAtLeast,
        liveFromPrice: payload.liveFromPrice || null,
        loadedFor: payload.fitment
          ? pageKey(state.pageType, payload.make, payload.model)
          : null,
      };
    case FETCH_SEO_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    case FETCH_DEMAND_CARS_SUCCESS:
      return { ...state, fetchInProgress: false, demandCars: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

// `make` and `model` let the reducer tell a refetch of the car already on
// screen apart from a navigation to a different car.
export const fetchSeoRequest = (pageType, make, model) => ({
  type: FETCH_SEO_REQUEST,
  payload: { pageType, make, model },
});

export const fetchSeoSuccess = ({
  fitment,
  relatedModels,
  listingRefs,
  make,
  model,
  liveTotal,
  liveTotalIsAtLeast,
  liveFromPrice,
}) => ({
  type: FETCH_SEO_SUCCESS,
  payload: {
    fitment,
    relatedModels,
    listingRefs,
    make,
    model,
    liveTotal,
    liveTotalIsAtLeast,
    liveFromPrice,
  },
});

export const fetchSeoError = e => ({
  type: FETCH_SEO_ERROR,
  error: true,
  payload: e,
});

export const fetchDemandCarsSuccess = cars => ({
  type: FETCH_DEMAND_CARS_SUCCESS,
  payload: cars,
});

// ================ Helpers ================ //

// Isomorphic fetch of our own SEO API. On the client apiBaseUrl() resolves to
// the dev API port / window origin; during SSR we pass the configured
// marketplace root URL so the absolute request resolves on the server.
const fetchSeoFitment = (make, model, config) => {
  const base = apiBaseUrl(config?.marketplaceRootURL);
  const url = `${base}/api/seo/fitment/${encodeURIComponent(make)}/${encodeURIComponent(model)}`;
  return fetch(url, { headers: { Accept: 'application/json' } }).then(res => {
    // A missing car is a normal, expected outcome — surface it as null so the
    // page can render a NotFound instead of throwing the whole load.
    if (res.status === 404) {
      return { fitment: null, relatedModels: [] };
    }
    if (!res.ok) {
      throw new Error(`SEO fitment fetch failed with status ${res.status}`);
    }
    return res.json();
  });
};

// ================ Thunks ================ //

/**
 * loadData for the /hjulpaket index page (P25009-408).
 *
 * The car list is the only thing this page needs, and it comes from our own
 * API, so this is a plain fetch rather than an SDK call. It never rejects: an
 * index page with no list is a thinner page, not a broken one, and it says so
 * in its own copy.
 */
export const loadDemandCars = () => (params, search, config) => dispatch => {
  dispatch(fetchSeoRequest('package'));

  const base = apiBaseUrl(config?.marketplaceRootURL);
  return fetch(`${base}/api/seo/demand-cars`, { headers: { Accept: 'application/json' } })
    .then(res => {
      if (!res.ok) {
        throw new Error(`SEO demand-cars fetch failed with status ${res.status}`);
      }
      return res.json();
    })
    .then(body => {
      dispatch(fetchDemandCarsSuccess(body?.cars || []));
      return body;
    })
    .catch(e => {
      dispatch(fetchSeoError(storableError(e)));
    });
};

// loadData is a factory bound to the page type so the same component can serve
// both /dack and /falgar routes with the right listing filter and copy.
export const loadData = pageType => (params, search, config) => (dispatch, getState, sdk) => {
  const { make, model } = params || {};
  dispatch(fetchSeoRequest(pageType, make, model));

  const adTypes = AD_TYPES_BY_PAGE_TYPE[pageType] || [];
  // Comma-separated enum value = "match any of" in the Marketplace API.
  const adTypeFilter = adTypes.length ? { pub_adType: adTypes.join(',') } : {};

  const isPackagePage = pageType === 'package';

  const listingParams = {
    pub_categoryLevel1: make,
    ...(model ? { pub_categoryLevel2: model } : {}),
    ...adTypeFilter,
    /* Ask for packages only. Does nothing unless a search schema exists for the
     * key — see PACKAGE_SCAN_SIZE — which is why nothing below trusts it
     * without checking. Harmless when ignored: the results are then simply the
     * same set the query would have returned anyway. */
    ...(isPackagePage ? { pub_isReachShell: true } : {}),
    pub_isDeleted: false,
    /* A hidden listing — a workshop's hidden trade-in included (P25009-485) —
     * is not for sale to the public; the main search filters the same way. */
    pub_isHide: false,
    /* P25009-410 — a catalogue row is not on the marketplace, so not on an
     * SEO page either: thin catalogue pages are exactly what the catalogue
     * exists to keep out of the index. */
    ...MARKETPLACE_FILTER,
    /* The answer page states how many packages exist and from what price, so it
     * reads a wider slice than it shows and works the figures out below. The
     * other page types only ever show a slice. */
    perPage: isPackagePage ? PACKAGE_SCAN_SIZE : RESULT_PAGE_SIZE,
    include: ['images'],
    'fields.listing': [
      'title',
      'price',
      'publicData.county',
      'publicData.adType',
      // Needed to build the breadcrumb listing URL — see util/listingSeoUrl.js.
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
      /* The package test needs both of these: the marker AND the bundle whose
       * parts prove the package can actually be fulfilled. Asking for the
       * marker alone would make isPackageListing reject every result. */
      'publicData.isReachShell',
      'publicData.reachBundle',
      'publicData.packageType',
      'publicData.tiretype',
    ],
    'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
    'limit.images': 1,
  };

  return Promise.all([
    fetchSeoFitment(make, model, config),
    sdk.listings.query(listingParams),
  ])
    .then(([seo, listingsResponse]) => {
      dispatch(addMarketplaceEntities(listingsResponse));

      const all = listingsResponse.data.data;
      /* The package test is applied here as well as asked for in the query. It
       * is the guarantee; the query parameter is only an optimisation, and it
       * does nothing at all without a search schema. On the other page types
       * every result counts. */
      const unsorted = isPackagePage ? all.filter(isPackageListing) : all;

      /* Did `pub_isReachShell` actually filter anything?
       *
       * If the schema exists, every result IS a package. If it does not, the API
       * ignored the parameter and handed back every complete-wheel listing for
       * this car, so some results are not packages. That is the test — no extra
       * request needed, and it answers the question for the environment the code
       * is really running in rather than the one it was written for.
       *
       * The awkward case is benign: a car whose every wheel listing happens to
       * be a package reads as "the filter worked", and the server count is then
       * the right answer anyway. */
      const serverTotal = listingsResponse.data.meta?.totalItems;
      const filterTookEffect = isPackagePage && all.length > 0 && unsorted.length === all.length;

      /* Cheapest first, sorted here rather than by the API. The page's own copy
       * promises the cheapest package is first, and `sort` on the Marketplace
       * API does not mean what it looks like it means — a plain field name is
       * DESCENDING there, which is the opposite of what this needs. Sorting the
       * page of results we already hold cannot be got wrong. */
      const relevant = isPackagePage
        ? [...unsorted].sort(
            (a, b) =>
              (a.attributes?.price?.amount ?? Infinity) -
              (b.attributes?.price?.amount ?? Infinity)
          )
        : unsorted;

      const listingRefs = relevant
        .slice(0, RESULT_PAGE_SIZE)
        .map(l => ({ id: l.id, type: 'listing' }));

      // The lowest price among the results. Money stays a Money — formatting
      // needs intl, which lives in the component.
      const prices = relevant.map(l => l.attributes?.price).filter(p => p?.amount != null);
      const liveFromPrice = prices.length
        ? prices.reduce((low, p) => (p.amount < low.amount ? p : low))
        : null;

      dispatch(
        fetchSeoSuccess({
          make,
          model,
          fitment: seo?.fitment || null,
          relatedModels: seo?.relatedModels || [],
          listingRefs,
          /* The server's count when it is a count of packages, otherwise what
           * we could see and check for ourselves. */
          liveTotal: isPackagePage
            ? filterTookEffect && serverTotal != null
              ? serverTotal
              : relevant.length
            : serverTotal || relevant.length,
          /* Only a floor when we had to count for ourselves AND the scan hit its
           * ceiling — the page then says "100+" instead of a number it cannot
           * stand behind. With a working filter the count is exact at any size,
           * so this is false however many packages there are. */
          liveTotalIsAtLeast:
            isPackagePage && !filterTookEffect && all.length >= PACKAGE_SCAN_SIZE,
          liveFromPrice,
        })
      );
      return seo;
    })
    .catch(e => {
      dispatch(fetchSeoError(storableError(e)));
    });
};
